import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { KeyRound, Loader2, Check, ArrowLeft, Copy, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, self-serve "Forgot password" entry. The user types either an email
 * or a phone number; we POST to /auth/request-password-reset.
 *
 * The backend always returns 200 with a generic "if a match exists" message
 * (no enumeration). When the account exists AND has an email on file AND
 * SendGrid is configured, the link is emailed automatically. Otherwise the
 * backend returns a `fallback_url` we display so the user can copy it into a
 * new tab — keeps the flow usable even before SendGrid/Twilio are wired.
 */
export default function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | success
  const [resetUrl, setResetUrl] = useState("");
  const [deliveredVia, setDeliveredVia] = useState([]);
  const [hasEmail, setHasEmail] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setError("");
    if (!identifier.trim()) { setError("Please enter your email or phone number"); return; }
    setSubmitting(true);
    try {
      const id = identifier.trim();
      const payload = id.includes("@") ? { email: id } : { phone: id };
      const res = await axios.post(`${API}/auth/request-password-reset`, payload);
      setResetUrl(res.data?.fallback_url || "");
      setDeliveredVia(res.data?.delivered_via || []);
      setHasEmail(!!res.data?.has_email_on_file);
      setStatus("success");
    } catch (e) {
      // Surface specific failure modes so users (and we) know what went wrong
      // instead of a generic "something broke" — which is usually network or
      // a stale cached bundle in production.
      let msg;
      if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e.response?.status) {
        msg = `Server returned ${e.response.status}. Please try again in a moment.`;
      } else if (e.code === "ECONNABORTED" || /timeout/i.test(e.message || "")) {
        msg = "Request timed out — check your internet connection and try again.";
      } else if (e.message === "Network Error") {
        msg = "Couldn't reach the server. If this persists, hard-refresh the page (Ctrl/Cmd+Shift+R) and try again.";
      } else {
        msg = e.message || "Something went wrong. Try again.";
      }
      setError(msg);
    }
    setSubmitting(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(resetUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50 flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs font-['Manrope'] font-semibold text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-login-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </Link>
        <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-slate-200/40 p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-6 h-6 text-rose-600" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Forgot your password?</h1>
              <p className="text-sm text-slate-500 font-['Manrope'] mt-1">Enter the email or phone number on your account and we'll send you a secure reset link.</p>
            </div>
          </div>

          {status === "idle" && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block font-['Manrope']">Email or phone number</label>
                <Input
                  data-testid="forgot-identifier-input"
                  type="text"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && identifier.trim()) submit(); }}
                  placeholder="you@salon.com or (555) 555-5555"
                  className="h-12 bg-slate-50 border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900 text-base"
                  autoFocus
                />
              </div>
              {error && (
                <div className="text-sm text-rose-600 font-['Manrope'] flex items-center gap-2" data-testid="forgot-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}
              <Button
                onClick={submit}
                disabled={submitting || !identifier.trim()}
                className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white rounded-full font-['Manrope'] font-semibold disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…</> : "Send reset link"}
              </Button>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4" data-testid="forgot-success">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-800 font-['Manrope'] text-sm">Reset link generated</p>
                  <p className="text-xs text-emerald-700/90 font-['Manrope'] mt-1 leading-relaxed">
                    If an account matches that contact info, a one-time link valid for 24 hours has been generated.
                  </p>
                </div>
              </div>

              {deliveredVia.includes("email") ? (
                <p className="text-sm text-slate-600 font-['Manrope'] leading-relaxed">
                  Check your <span className="font-semibold text-slate-900">inbox</span> (and spam folder). Click the link in the email to set a new password.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 font-['Manrope'] leading-relaxed">
                    {hasEmail
                      ? "Email delivery isn't fully wired up yet — copy this one-time link and paste it into your address bar to continue."
                      : "We don't have an email on file for that account, so copy this one-time link and paste it into your address bar to continue."}
                  </p>
                  {resetUrl && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-2">
                      <code className="text-[11px] font-mono text-slate-700 truncate flex-1" data-testid="forgot-fallback-url">{resetUrl}</code>
                      <button
                        onClick={copy}
                        className="text-[11px] font-semibold uppercase tracking-wider text-rose-600 hover:text-rose-700 flex items-center gap-1 flex-shrink-0 font-['Manrope']"
                        data-testid="forgot-copy-link-btn"
                      >
                        {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 border-t border-slate-100">
                <Link to="/login" className="text-xs font-['Manrope'] font-semibold text-slate-500 hover:text-slate-700">
                  Back to login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
